import type { PosCommandView, PosServices, PosSession, PosShiftView } from '../posContracts';
import { api } from './client';

const P = '/api/pos';
const paise = (rupees: number) => Math.round(Number(rupees) * 100);

type ApiShift = {
  id: string; status: string; openedAt: string; closedAt?: string | null;
  floats?: { opening?: number }; totals?: { gross?: number; cash?: number; net?: number };
  cashierName?: string; grossSales?: number; cashIn?: number; netSales?: number;
};

function shiftView(row: ApiShift, outletName: string, cashierName: string): PosShiftView {
  return {
    id: row.id,
    status: row.status.toLowerCase(),
    outletName,
    cashierName: row.cashierName ?? cashierName,
    openedAt: row.openedAt,
    closedAt: row.closedAt,
    openingFloat: paise(Number(row.floats?.opening ?? 0)),
    grossSales: paise(row.grossSales ?? row.totals?.gross ?? 0),
    cashIn: paise(row.cashIn ?? row.totals?.cash ?? 0),
    cashOut: 0,
    netSales: paise(row.netSales ?? row.totals?.net ?? 0),
  };
}

export const httpPosServices: PosServices = {
  async session(): Promise<PosSession> {
    const [session, catalog] = await Promise.all([
      api<{ outlet: { name: string }; cashier: { name: string }; shift: ApiShift | null; taxRate?: number }>(`${P}/session`),
      api<{ items: { id: string; name: string; sku: string; price: number; stock: number; category: string }[] }>(`${P}/catalog`),
    ]);
    return {
      taxRate: session.taxRate ?? 18,
      outletName: session.outlet.name,
      shift: session.shift ? shiftView(session.shift, session.outlet.name, session.cashier.name) : null,
      products: (catalog.items ?? []).map((p) => ({ ...p, price: paise(p.price) })),
    };
  },
  async openShift(openingFloatPaise) {
    const session = await api<{ outlet: { name: string }; cashier: { name: string } }>(`${P}/session`);
    const row = await api<ApiShift>(`${P}/shifts/open`, {
      method: 'POST', body: JSON.stringify({ openingFloat: Math.round(openingFloatPaise / 100) }),
    });
    return shiftView(row, session.outlet.name, session.cashier.name);
  },
  async closeShift(closingFloatPaise) {
    await api(`${P}/shifts/close`, { method: 'POST', body: JSON.stringify({ closingFloat: Math.round(closingFloatPaise / 100) }) });
  },
  async sale(items, tender) {
    await api(`${P}/sales`, { method: 'POST', body: JSON.stringify({ items, tender }) });
  },
  async history() {
    const page = await api<{ shifts: ApiShift[] }>(`${P}/history`);
    return (page.shifts ?? []).map((s) => shiftView(s, '', s.cashierName ?? 'Cashier'));
  },
  async queue() {
    const page = await api<{ items: { id: string; type: string; status: string; createdAt: string; shiftId?: string }[] }>(`${P}/offline/queue`);
    return (page.items ?? []).map((c): PosCommandView => ({
      id: c.id, type: c.type, status: c.status.toLowerCase(), createdAt: c.createdAt, shiftId: c.shiftId ?? '',
    }));
  },
  async sync(commands) {
    await api(`${P}/offline/sync`, { method: 'POST', body: JSON.stringify({ deviceId: 'demo-terminal', commands }) });
  },
};
